import type { AdminOrderData } from "../page";

interface Props {
  order: AdminOrderData;
}

export default function AdminOrderFinancials({ order }: Props) {
  const totalSavings = order.discountAmount + (order.redeemCoupon?.discountAmount ?? 0);

  return (
    <div className="bg-white border border-gray-100 rounded-2xl p-6">
      <h3 className="font-bold text-[#1a1a1a] text-sm mb-4 flex items-center gap-2">
        <div className="w-7 h-7 flex items-center justify-center bg-gray-50 rounded-lg">
          <i className="ri-receipt-line text-[#1a1a1a] text-sm"></i>
        </div>
        ملخص مالي
      </h3>

      <div className="flex flex-col gap-3">
        {/* Subtotal */}
        <div className="flex justify-between items-center py-2 border-b border-gray-50">
          <span className="text-sm text-gray-500">المجموع الفرعي</span>
          <span className="text-sm font-bold text-[#1a1a1a]">{order.totalPrice.toLocaleString("ar-SA")} ر.س</span>
        </div>

        {/* Discount code */}
        {order.discountAmount > 0 && (
          <div className="flex justify-between items-center py-2 border-b border-gray-50">
            <span className="text-sm text-gray-500 flex items-center gap-2">
              <i className="ri-coupon-3-line text-green-500 text-sm"></i>
              كود الخصم
              {order.discountCode && (
                <span className="bg-green-50 text-green-700 text-[11px] font-bold px-2 py-0.5 rounded-full border border-green-200">
                  {order.discountCode} ({order.discountPercent}%)
                </span>
              )}
            </span>
            <span className="text-sm font-bold text-green-600">— {order.discountAmount.toLocaleString("ar-SA")} ر.س</span>
          </div>
        )}

        {/* Redeem coupon */}
        {order.redeemCoupon && (
          <div className="flex justify-between items-center py-2 border-b border-gray-50">
            <span className="text-sm text-gray-500 flex items-center gap-2">
              <i className="ri-exchange-dollar-line text-amber-500 text-sm"></i>
              كوبون نقاط
              <span className="bg-amber-50 text-amber-700 text-[11px] font-bold px-2 py-0.5 rounded-full border border-amber-200 font-mono">
                {order.redeemCoupon.couponCode}
              </span>
            </span>
            <span className="text-sm font-bold text-amber-600">— {order.redeemCoupon.discountAmount.toLocaleString("ar-SA")} ر.س</span>
          </div>
        )}

        {/* Shipping */}
        <div className="flex justify-between items-center py-2 border-b border-gray-50">
          <span className="text-sm text-gray-500 flex items-center gap-2">
            <i className="ri-truck-line text-gray-400 text-sm"></i>
            الشحن
          </span>
          <span className="text-sm font-bold text-green-600">مجاني</span>
        </div>

        {/* Payment method */}
        <div className="flex justify-between items-center py-2 border-b border-gray-50">
          <span className="text-sm text-gray-500 flex items-center gap-2">
            <i className="ri-hand-coin-line text-gray-400 text-sm"></i>
            طريقة الدفع
          </span>
          <span className="text-sm font-bold text-[#1a1a1a]">{order.paymentLabel}</span>
        </div>

        {/* Total */}
        <div className="flex justify-between items-center pt-2">
          <span className="text-base font-bold text-[#1a1a1a]">الإجمالي النهائي</span>
          <div className="text-left">
            <span className="text-xl font-black text-[#1a1a1a]">{order.finalPrice.toLocaleString("ar-SA")}</span>
            <span className="text-sm text-gray-400 mr-1">ر.س</span>
          </div>
        </div>

        {/* Savings */}
        {totalSavings > 0 && (
          <div className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-xl px-4 py-3 mt-1">
            <i className="ri-price-tag-3-line text-green-600 text-sm"></i>
            <p className="text-green-700 text-sm font-bold">
              وفّر العميل {totalSavings.toLocaleString("ar-SA")} ر.س في هذا الطلب
            </p>
          </div>
        )}

        {/* Points earned */}
        {order.pointsEarned && order.pointsEarned > 0 && (
          <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
            <i className="ri-vip-crown-line text-amber-600 text-sm"></i>
            <p className="text-amber-700 text-sm font-bold">
              كسب العميل <span className="text-base">+{order.pointsEarned}</span> نقطة مكافأة
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
